import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Mail, Clock, ShieldCheck, HeartHandshake } from 'lucide-react';
import { COMPANY } from '@/config/company';

export default function CompanyInfo() {
  return (
    <section id="company" className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
              About Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              A lender committed to responsible credit
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              {COMPANY.name} is a technology-led consumer lending company helping people borrow with
              clarity. We publish our rates, terms and fees up front, and we explain the total cost of
              credit before you sign anything.
            </p>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Every application is subject to a credit and affordability assessment. We lend only when
              we believe repayments are sustainable for you — approval is never guaranteed, and we will
              decline an application when borrowing is not in your interest.
            </p>
            <div className="space-y-3">
              <p className="flex gap-3 text-foreground">
                <ShieldCheck className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <span>Clear disclosures, no hidden charges, no prepayment penalties.</span>
              </p>
              <p className="flex gap-3 text-foreground">
                <HeartHandshake className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <span>Support for customers in financial difficulty — talk to us early.</span>
              </p>
            </div>
          </div>

          <Card className="shadow-card">
            <CardContent className="p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-foreground mb-6">Company information</h3>
              <dl className="space-y-5 text-sm">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <dt className="font-medium text-foreground">Registered office</dt>
                    <dd className="text-muted-foreground">
                      {COMPANY.legalName}<br />
                      {COMPANY.addressLine1}<br />
                      {COMPANY.addressLine2}
                    </dd>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Phone className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <dt className="font-medium text-foreground">Phone</dt>
                    <dd>
                      <a href={COMPANY.phoneHref} className="text-muted-foreground hover:text-accent transition-colors">
                        {COMPANY.phone}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <dt className="font-medium text-foreground">Email</dt>
                    <dd>
                      <a href={`mailto:${COMPANY.email}`} className="text-muted-foreground hover:text-accent transition-colors">
                        {COMPANY.email}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Clock className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <dt className="font-medium text-foreground">Business hours</dt>
                    <dd className="text-muted-foreground space-y-0.5">
                      {COMPANY.hours.map((h) => (
                        <div key={h.days} className="flex gap-2">
                          <span className="min-w-[9rem]">{h.days}</span>
                          <span>{h.time}</span>
                        </div>
                      ))}
                    </dd>
                  </div>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
